import type { SpatialNode } from "@/features/canvas/engine/primitives/geometry/geometry";

interface AbsolutePos {
  x: number;
  y: number;
}

interface RFNodeLike {
  id: string;
  type?: string;
  position: AbsolutePos;
  width?: number;
  height?: number;
  parentId?: string;
}

/**
 * Project React Flow nodes (carrying parent-relative positions for nested
 * children) into the absolute-position SpatialNode shape geometry helpers
 * expect.
 *
 * `getCachedAbsolute` is the frame-deleted-mid-drag fallback (client.md §6).
 * When walking a node's parent chain we look up each ancestor in the live
 * `nodes` list; if an ancestor is missing we consult the cache, and if the
 * cache returns a position we treat it as the already-resolved absolute (the
 * cache stores absolute positions snapshotted at drag-start), which short-
 * circuits the walk. If neither the live list nor the cache knows the
 * ancestor, we degrade gracefully: stop walking and use whatever offset has
 * accumulated so far. The visible child may be misplaced but the projection
 * doesn't crash.
 */
export function projectSpatialNodes(
  nodes: readonly RFNodeLike[],
  getCachedAbsolute: (parentId: string) => AbsolutePos | null,
): SpatialNode[] {
  const byId = new Map(nodes.map(n => [n.id, n]));
  return nodes.map(n => {
    let absX = n.position.x;
    let absY = n.position.y;
    let parentId = n.parentId;
    while (parentId) {
      const parent = byId.get(parentId);
      if (parent) {
        absX += parent.position.x;
        absY += parent.position.y;
        parentId = parent.parentId;
        continue;
      }
      // Parent missing from the live list — try the drag cache.
      const cached = getCachedAbsolute(parentId);
      if (cached) {
        // Cached value is already the parent's absolute position at
        // drag-start. We've been accumulating frame-local offsets in
        // (absX, absY), so add the cached absolute and stop walking.
        absX += cached.x;
        absY += cached.y;
      }
      break;
    }
    return {
      id: n.id,
      type: n.type ?? 'unknown',
      position: { x: absX, y: absY },
      size:
        n.width !== undefined && n.height !== undefined
          ? { width: n.width, height: n.height }
          : null,
      parentId: n.parentId ?? null,
    };
  });
}
